import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Gift,
  Loader2,
  Send,
  Sparkles,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui";
import { DetailSkeleton } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCampaignMutations,
  useMarketplaceCampaign,
  type PublishFailure,
} from "@/hooks/useMarketplaceCampaigns";
import { splitCommission } from "./campaignConstants";
import { CAMPAIGN_TONE } from "./statusTone";
import { COUNTRY_LABELS, PLATFORM_LABELS } from "./constants";
import ApplyPanel from "./components/ApplyPanel";
import CampaignApplications from "./components/CampaignApplications";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
        {label}
      </p>
      <div className="text-sm text-neutral-900 mt-0.5">{children}</div>
    </div>
  );
}

export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { campaign, loading, refetch } = useMarketplaceCampaign(campaignId);
  const { publishCampaign, cancelCampaign, deleteCampaign, busy } = useCampaignMutations();
  const [failure, setFailure] = useState<PublishFailure | null>(null);

  const isBrand = user?.marketplaceRole !== "CREATOR";

  if (loading && !campaign) {
    return (
      <div className="adv-page">
        <DetailSkeleton />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-sm font-medium text-neutral-900">
          {t("marketplace.campaign.notFoundTitle")}
        </p>
        <p className="text-sm text-neutral-500 mt-1">{t("marketplace.campaign.notFoundBody")}</p>
        <Link
          to="/campanas"
          className="inline-block mt-4 text-sm font-medium text-steel-600 hover:text-steel-700"
        >
          {t("marketplace.campaign.backToList")}
        </Link>
      </div>
    );
  }

  const onPublish = async () => {
    setFailure(null);
    try {
      await publishCampaign(campaign.id);
      await refetch();
    } catch (e) {
      setFailure(e as PublishFailure);
    }
  };

  const onDelete = async () => {
    await deleteCampaign(campaign.id);
    navigate("/campanas", { replace: true });
  };

  const split =
    campaign.budgetTotal !== null
      ? splitCommission(campaign.budgetTotal, campaign.commissionPct)
      : null;

  const canPublish = campaign.status === "DRAFT" && campaign.missingForPublish.length === 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <Link
        to="/campanas"
        className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("marketplace.campaign.backToList")}
      </Link>

      <div className="bg-white border border-neutral-200 rounded-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-neutral-900">{campaign.title}</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              {campaign.brandProfile?.companyName ??
                t(`marketplace.objectives.${campaign.objective}`)}
            </p>
          </div>
          <Badge variant={CAMPAIGN_TONE[campaign.status]} stamp className="shrink-0">
            {t(`marketplace.campaignStatus.${campaign.status}`)}
          </Badge>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-5 pt-5 border-t border-neutral-100">
          <Field label={t("marketplace.campaign.compensation")}>
            <span className="inline-flex items-center gap-1">
              {campaign.compensationType !== "CASH" && (
                <Gift className="w-3.5 h-3.5 text-emerald-600" />
              )}
              {t(`marketplace.compensation.${campaign.compensationType}`)}
            </span>
          </Field>
          <Field label={t("marketplace.campaign.budget")}>
            {campaign.budgetTotal !== null
              ? `${campaign.currency} ${campaign.budgetTotal.toLocaleString("es")}`
              : "—"}
          </Field>
          <Field label={t("marketplace.campaign.creatorCount")}>
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-neutral-400" />
              {campaign.targetCreatorCount ?? "—"}
            </span>
          </Field>
        </div>

        {/* Only the brand sees the fee breakdown — a creator seeing the
            platform's cut on a campaign they have not been offered is noise. */}
        {isBrand && split && (
          <div className="mt-4 pt-4 border-t border-neutral-100 text-xs text-neutral-500 space-y-0.5">
            <p>
              {t("marketplace.campaign.creatorGets")}{" "}
              <strong className="text-neutral-900">
                {campaign.currency} {split.creatorPayout.toLocaleString("es")}
              </strong>
            </p>
            <p>
              {t("marketplace.campaign.platformFee")} ({campaign.commissionPct}%){" "}
              <strong className="text-neutral-900">
                {campaign.currency} {split.commission.toLocaleString("es")}
              </strong>
            </p>
            <p className="pt-1 border-t border-neutral-100">
              {t("marketplace.campaign.youPay")}{" "}
              <strong className="text-neutral-900">
                {campaign.currency} {split.brandTotal.toLocaleString("es")}
              </strong>
            </p>
          </div>
        )}
      </div>

      {/* Brief */}
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
        <h2 className="text-sm font-semibold text-neutral-900 mb-3">
          {t("marketplace.campaign.brief")}
        </h2>

        {campaign.briefMarkdown ? (
          <div className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">
            {campaign.briefMarkdown}
          </div>
        ) : (
          <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-steel-600/5 border border-steel-600/20">
            <Sparkles className="w-4 h-4 text-steel-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-steel-700 leading-relaxed">
              {isBrand
                ? t("marketplace.campaign.noBriefBrand")
                : t("marketplace.campaign.noBriefCreator")}
            </p>
          </div>
        )}
      </div>

      {/* Deliverables + targeting */}
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4 space-y-4">
        <div>
          <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-2">
            {t("marketplace.campaign.deliverables")}
          </p>
          {campaign.deliverablesSpec?.length ? (
            <ul className="space-y-1">
              {campaign.deliverablesSpec.map((d, i) => (
                <li key={i} className="text-sm text-neutral-700">
                  <strong className="text-neutral-900">{d.quantity}×</strong>{" "}
                  {t(`marketplace.formats.${d.format}`)} · {PLATFORM_LABELS[d.platform]}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutral-400">—</p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-neutral-100">
          <Field label={t("marketplace.campaign.targetCountries")}>
            {campaign.targetCountries.length
              ? campaign.targetCountries.map((c) => COUNTRY_LABELS[c] ?? c).join(", ")
              : t("marketplace.campaign.anywhere")}
          </Field>
          <Field label={t("marketplace.campaign.targetNiches")}>
            {campaign.targetNiches.length
              ? campaign.targetNiches
                  .map((n) => t(`marketplace.niches.${n}`, { defaultValue: n }))
                  .join(", ")
              : t("marketplace.campaign.anyNiche")}
          </Field>
        </div>

        {campaign.applicationDeadline && (
          <div className="pt-4 border-t border-neutral-100">
            <Field label={t("marketplace.campaign.deadline")}>
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-neutral-400" />
                {new Date(campaign.applicationDeadline).toLocaleDateString("es")}
              </span>
            </Field>
          </div>
        )}
      </div>

      {/* Brand actions */}
      {isBrand && campaign.status === "DRAFT" && (
        <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
          {campaign.missingForPublish.length > 0 && (
            <div className="flex items-start gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900">
                  {t("marketplace.campaign.notReady")}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {campaign.missingForPublish.map((m) => (
                    <li key={m} className="text-xs text-amber-700">
                      • {t(`marketplace.campaign.missing.${m}`, { defaultValue: m })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {failure && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2 mb-4">
              {failure.message}
            </p>
          )}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="text-xs font-medium text-neutral-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-40"
            >
              {t("marketplace.campaign.deleteDraft")}
            </button>

            <button
              type="button"
              onClick={onPublish}
              disabled={busy || !canPublish}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-sm font-medium hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t("marketplace.campaign.publish")}
            </button>
          </div>
        </div>
      )}

      {/* Applications live on the campaign they are about. The collaboration
          inbox tracks what happens after one is accepted. */}
      {isBrand && campaign.status !== "DRAFT" && (
        <CampaignApplications campaignId={campaign.id} currency={campaign.currency} />
      )}

      {!isBrand && (
        <ApplyPanel
          campaignId={campaign.id}
          currency={campaign.currency}
          commissionPct={campaign.commissionPct}
          campaignIsOpen={campaign.status === "OPEN"}
        />
      )}

      {isBrand && campaign.status === "OPEN" && (
        <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4 flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-500">{t("marketplace.campaign.liveNotice")}</p>
          <button
            type="button"
            onClick={() => cancelCampaign(campaign.id).then(() => refetch())}
            disabled={busy}
            className="text-xs font-medium text-neutral-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-40 flex-shrink-0"
          >
            {t("marketplace.campaign.cancel")}
          </button>
        </div>
      )}
    </div>
  );
}
